// src/pages/Login.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { backendUrl } from "../utils/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const recaptchaRef = useRef(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Detect non-laptop/desktop devices (tablet/phone)
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // Capture redirect param (?from=/docwriter/start etc.)
  const redirectPath = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("from");
  }, [location.search]);

  const getFreshToken = async () => {
    if (!recaptchaRef.current) {
      throw new Error("captcha-not-ready");
    }

    const EXEC_TIMEOUT_MS = 15000;

    const execPromise = recaptchaRef.current.executeAsync();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("captcha-timeout")), EXEC_TIMEOUT_MS)
    );

    try {
      return await Promise.race([execPromise, timeoutPromise]);
    } finally {
      try {
        recaptchaRef.current.reset();
      } catch {}
    }
  };

  const getFriendlyLoginError = (err) => {
    const code = err?.code || "";
    const message = String(err?.message || "").toLowerCase();

    // Firebase often returns auth/invalid-credential for both wrong password and unknown email.
    if (
      code === "auth/invalid-credential" ||
      code === "auth/user-not-found" ||
      code === "auth/wrong-password" ||
      message.includes("invalid-credential") ||
      message.includes("wrong-password") ||
      message.includes("user-not-found")
    ) {
      return "Email or password is incorrect.";
    }

    if (code === "auth/invalid-email" || message.includes("invalid-email")) {
      return "Please enter a valid email address.";
    }

    if (code === "auth/user-disabled" || message.includes("user-disabled")) {
      return "This account has been disabled. Please contact support.";
    }

    if (code === "auth/too-many-requests" || message.includes("too-many-requests")) {
      return "Too many failed login attempts. Please wait and try again.";
    }

    if (code === "auth/network-request-failed" || message.includes("network-request-failed")) {
      return "Network error. Please check your connection and try again.";
    }

    if (code === "auth/missing-password" || message.includes("missing-password")) {
      return "Please enter your password.";
    }

    return "Unable to log in. Please check your information and try again.";
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    const cleanEmail = (email || "").trim().toLowerCase();
    if (!cleanEmail || !password) {
      setError("Please enter both email and password.");
      return;
    }

    if (!SITE_KEY_V2) {
      setError("reCAPTCHA not configured.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Invisible reCAPTCHA gate before any Firebase sign-in attempt.
      // The token is generated fresh and verified by our backend first.
      const recaptchaToken = await getFreshToken();

      const captchaRes = await fetch(
        `${backendUrl}/auth/verify-login-recaptcha`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recaptchaToken }),
        }
      );

      const captchaData = await captchaRes.json().catch(() => ({}));

      if (!captchaRes.ok || captchaData.success === false) {
        throw new Error(
          captchaData.error ||
            captchaData.message ||
            "reCAPTCHA verification failed. Please try again."
        );
      }

      const auth = getAuth();

      // ✅ Step 1: Firebase sign-in (fast)
      // Wrong email/password errors happen here, before the backend login route runs.
      const result = await signInWithEmailAndPassword(auth, cleanEmail, password);

      // ✅ If the email is not verified yet, send the user back to verification flow.
      if (!result.user.emailVerified) {
        localStorage.setItem("signup_email", cleanEmail);
        setError("Please verify your email before logging in.");
        navigate("/check-your-email");
        return;
      }

      // ✅ Step 2: Only run backend OTP gate for internal users (employees)
      // Public users should NOT wait on backend login scanning.
      const isInternal = cleanEmail.endsWith("@asklawly.com");

      if (isInternal) {
        const idToken = await result.user.getIdToken(true);

        const res = await fetch(`${backendUrl}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });

        const response = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(response.error || "Login failed. Please try again.");
          return;
        }

        if (response.otp_required) {
          localStorage.setItem("signup_email", cleanEmail);
          navigate("/verify-otp");
          return;
        }
      }

      // ✅ Step 3: Navigate immediately
      navigate(redirectPath || "/dashboard");
    } catch (err) {
      console.error("Login error:", err);

      const message = String(err?.message || "");
      if (
        message.includes("captcha") ||
        message.includes("Timeout") ||
        message.includes("timeout")
      ) {
        setError("reCAPTCHA expired or failed. Please try again.");
      } else {
        setError(getFriendlyLoginError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Same vertical start; wider on phones/tablets like Signup */}
      <div style={{ ...styles.outerWrap, ...(isSmallScreen ? styles.outerWrapCompact : null) }}>
        <div style={{ ...styles.card, ...(isSmallScreen ? styles.cardWide : null) }}>
          <h2 style={styles.title}>Login</h2>

          <form onSubmit={handleLogin} style={styles.form} noValidate>
            <div style={styles.inputWrap}>
              <input
                type="email"
                placeholder={t("email")}
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={{ ...styles.passwordContainer, ...styles.inputWrap }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("password")}
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeInsideBox}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div style={styles.forgotPassword}>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <div style={{ height: 0, overflow: "hidden" }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={SITE_KEY_V2}
                size="invisible"
              />
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? "Logging in..." : t("login")}
            </button>
          </form>

          {/* Desktop: one line; Phone/Tablet: two centered lines */}
          {isSmallScreen ? (
            <div style={styles.signupPromptMobile}>
              <div>Don’t have an account?</div>
              <div>
                <Link to="/signup" style={styles.signupLink}>
                  Create your free account today
                </Link>
                .
              </div>
            </div>
          ) : (
            <div style={styles.signupPromptDesktop}>
              Don’t have an account?{" "}
              <Link to="/signup" style={styles.signupLink}>
                Create your free account today
              </Link>
              .
            </div>
          )}

          <div style={styles.footerLinks}>
            <Link to="/terms" style={styles.footerLink}>
              Terms of Use
            </Link>
            <span style={{ margin: "0 10px" }}>|</span>
            <Link to="/privacy" style={styles.footerLink}>
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  outerWrap: {
    maxWidth: 960,
    margin: "24px auto",
    padding: "0 16px 40px",
  },
  // 📱 Trim side padding on small screens (same as Signup)
  outerWrapCompact: {
    padding: "0 8px 40px",
  },
  card: {
    maxWidth: 440,
    margin: "0 auto",
    padding: 28,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "sans-serif",
  },
  // 📱 Allow wider card cap on small screens (same as Signup)
  cardWide: {
    maxWidth: 520,
  },
  title: {
    textAlign: "center",
    fontSize: 26,
    fontWeight: "bold",
    marginBottom: 20,
    color: "#111827",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    alignItems: "stretch",
  },
  inputWrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  input: {
    padding: 12,
    fontSize: 16,
    border: "1px solid #ccc",
    borderRadius: 10,
    outline: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    width: "100%",
    boxSizing: "border-box",
  },
  passwordContainer: { position: "relative" },
  eyeInsideBox: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    color: "#888",
    fontSize: 18,
  },
  forgotPassword: {
    textAlign: "right",
    fontSize: 14,
    width: "100%",
  },
  button: {
    padding: 12,
    fontSize: 16,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    width: "100%",
    fontWeight: 700,
  },
  error: {
    color: "red",
    fontSize: 14,
    textAlign: "center",
  },
  signupPromptDesktop: {
    marginTop: 20,
    fontSize: 14,
  },
  signupPromptMobile: {
    marginTop: 20,
    fontSize: 14,
    textAlign: "center",
    lineHeight: 1.4,
  },
  signupLink: {
    color: "#1E2A78",
    textDecoration: "underline",
    fontWeight: "bold",
  },
  footerLinks: {
    marginTop: 20,
    fontSize: 13,
    textAlign: "center",
  },
  footerLink: {
    color: "#1E2A78",
    textDecoration: "underline",
  },
};

export default Login;
